import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft, Plus, Send, ExternalLink, Copy, Download, Trash2,
  FileCheck2, FileClock, MessageSquare, Loader2, ScrollText, Image as ImageIcon, Check, Eye, Mail,
  BarChart3, Activity, Shield, ShieldAlert, ShieldCheck,
  Calendar as CalendarIcon, Clock, X as XIcon, GripVertical, Building2,
} from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';
import RichTextEditor from '../components/RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  partnership: { label: 'Partnership', color: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
  venue_rental: { label: 'Venue Rental', color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  rfp: { label: 'RFP', color: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30' },
};

const STATUS_META = {
  draft: { label: 'Draft', color: 'bg-muted text-muted-foreground' },
  sent: { label: 'Sent', color: 'bg-sky-500/20 text-sky-300 border-sky-500/30' },
  viewed: { label: 'Viewed', color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  approved: { label: 'Approved', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
  declined: { label: 'Declined', color: 'bg-rose-500/20 text-rose-300 border-rose-500/30' },
};

export default function AdminVenueProposals({ embedded = false, eventId: eventIdProp }) {
  const params = useParams();
  const eventId = eventIdProp || params.eventId;
  const navigate = useNavigate();
  const { token } = useAuth();
  const confirm = useConfirm();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/venue-proposals`, auth);
      setProposals(res.data.proposals || []);
    } catch (e) {
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleCreate = async (draft) => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/venue-proposals`, draft, auth);
      toast.success('Proposal drafted');
      setEditing(res.data);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create proposal');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (pid) => {
    const ok = await confirm({
      title: 'Delete this proposal?',
      description: 'This permanently removes the proposal and its signed agreement.',
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/venue-proposals/${pid}`, auth);
      toast.success('Proposal deleted');
      refresh();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleDuplicate = async (proposal) => {
    const ok = await confirm({
      title: `Duplicate "${proposal.title}"?`,
      description: 'Creates a fresh draft with all content (executive summary, responsibilities, proforma, hero/flyer images) carried over. Status, comments, signature, and venue contact details are reset so you can pitch a different venue partner.',
      confirmLabel: 'Duplicate',
    });
    if (!ok) return;
    try {
      const res = await axios.post(`${API}/venue-proposals/${proposal.proposal_id}/duplicate`, {}, auth);
      const newId = res.data?.proposal_id;
      toast.success('Proposal duplicated — open the copy to fill in the new venue.');
      await refresh();
      if (newId) {
        setEditing(res.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to duplicate proposal');
    }
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-background pt-20 pb-12'}>
      <div className={embedded ? 'space-y-6' : 'max-w-7xl mx-auto px-4 space-y-6'} data-testid="venue-proposals-page">
        <div className="flex items-center justify-between gap-4">
          <div>
            {!embedded && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/events')} data-testid="back-to-events">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to Events
              </Button>
            )}
            <h1 className={`${embedded ? 'text-2xl' : 'text-3xl mt-2'} font-bold font-['Space_Grotesk'] flex items-center gap-3`}>
              <ScrollText className={embedded ? 'h-6 w-6 text-fuchsia-400' : 'h-7 w-7 text-fuchsia-400'} />
              Venue Proposals
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Generate professional partnership, rental, or RFP proposals for this event.
            </p>
          </div>
          <NewProposalButton onCreate={handleCreate} creating={creating} />
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-12"><Loader2 className="animate-spin h-6 w-6 mx-auto" /></div>
        ) : proposals.length === 0 ? (
          <EmptyState onCreate={handleCreate} creating={creating} />
        ) : (
          <div className="grid gap-3">
            {proposals.map((p) => (
              <ProposalRow key={p.proposal_id} p={p} onEdit={() => setEditing(p)} onDuplicate={() => handleDuplicate(p)} onDelete={() => handleDelete(p.proposal_id)} />
            ))}
          </div>
        )}

        {editing && (
          <ProposalEditor
            proposal={editing}
            token={token}
            onClose={() => { setEditing(null); refresh(); }}
            onSaved={(p) => setEditing(p)}
          />
        )}
      </div>
    </div>
  );
}

function EmptyState({ onCreate, creating }) {
  return (
    <Card className="border-dashed border-border bg-card/50">
      <CardContent className="p-10 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-fuchsia-500/10 flex items-center justify-center mx-auto">
          <ScrollText className="h-8 w-8 text-fuchsia-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">No proposals yet</h3>
          <p className="text-sm text-muted-foreground mt-1">Create your first venue proposal — it auto-pulls the event's hero, dates, overview and media kit.</p>
        </div>
        <NewProposalButton onCreate={onCreate} creating={creating} />
      </CardContent>
    </Card>
  );
}

function NewProposalButton({ onCreate, creating }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    proposal_type: 'partnership',
    venue_name: '',
    venue_email: '',
    venue_contact_name: '',
    include_proforma: true,
    proforma_mode: 'full_budget',
    venue_revenue_share_pct: 0,
    venue_rental_fee_cents: 0,
  });
  const submit = async () => {
    await onCreate(form);
    setOpen(false);
  };
  return (
    <>
      <Button onClick={() => setOpen(true)} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="new-proposal-btn">
        <Plus className="h-4 w-4 mr-1" /> New proposal
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader><DialogTitle>New Venue Proposal</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Proposal Type">
              <Select value={form.proposal_type} onValueChange={(v) => setForm({ ...form, proposal_type: v })}>
                <SelectTrigger data-testid="new-proposal-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="partnership">Partnership — revenue share</SelectItem>
                  <SelectItem value="venue_rental">Venue Rental — flat fee</SelectItem>
                  <SelectItem value="rfp">Request for Proposal</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Venue Name">
                <Input value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} placeholder="Union Station" data-testid="new-proposal-venue-name" />
              </Field>
              <Field label="Contact Name">
                <Input value={form.venue_contact_name} onChange={(e) => setForm({ ...form, venue_contact_name: e.target.value })} placeholder="Alex Lee" />
              </Field>
            </div>
            <Field label="Venue Email">
              <Input type="email" value={form.venue_email} onChange={(e) => setForm({ ...form, venue_email: e.target.value })} placeholder="events@venue.com" />
            </Field>
            <Field label="Contact Phone">
              <Input value={form.venue_contact_phone || ''} onChange={(e) => setForm({ ...form, venue_contact_phone: e.target.value })} placeholder="720-555-0100" data-testid="new-proposal-phone" />
            </Field>
            {form.proposal_type === 'partnership' && (
              <Field label="Venue revenue share %">
                <Input type="number" min="0" max="100" step="0.5" value={form.venue_revenue_share_pct} onChange={(e) => setForm({ ...form, venue_revenue_share_pct: parseFloat(e.target.value || 0) })} />
              </Field>
            )}
            {form.proposal_type === 'venue_rental' && (
              <Field label="Proposed rental fee ($)">
                <Input type="number" min="0" step="1" value={(form.venue_rental_fee_cents / 100) || ''} onChange={(e) => setForm({ ...form, venue_rental_fee_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} />
              </Field>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={creating} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="create-proposal-submit">
              {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Create & Open
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ProposalRow({ p, onEdit, onDuplicate, onDelete }) {
  const type = TYPE_META[p.proposal_type] || TYPE_META.partnership;
  const status = STATUS_META[p.status] || STATUS_META.draft;
  const viewPublic = () => window.open(`/venue-proposal/${p.token}`, '_blank', 'noopener,noreferrer');
  return (
    <Card className="hover:border-fuchsia-500/40 transition-colors" data-testid={`proposal-row-${p.proposal_id}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
          {p.hero_image_url
            ? <img src={p.hero_image_url} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon className="h-6 w-6" /></div>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold truncate">{p.title}</h3>
            <Badge className={`text-[10px] ${type.color}`}>{type.label}</Badge>
            <Badge className={`text-[10px] ${status.color}`}>{status.label}</Badge>
            {p.signature?.signed && <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300 border-emerald-500/30"><Check className="h-3 w-3 mr-0.5" />Signed</Badge>}
          </div>
          <p className="text-xs text-muted-foreground mt-1 truncate">
            {p.venue_name || '—'} · {p.venue_email || 'No email yet'}
            {p.viewed_at && ` · viewed ${new Date(p.viewed_at).toLocaleDateString()}`}
          </p>
          {(p.comments?.length || 0) > 0 && (
            <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
              <MessageSquare className="h-3 w-3" /> {p.comments.length} comment{p.comments.length > 1 ? 's' : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={viewPublic} title="View public landing" data-testid={`view-proposal-${p.proposal_id}`}>
            <Eye className="h-4 w-4 text-indigo-400" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`edit-proposal-${p.proposal_id}`}>Edit</Button>
          <Button variant="ghost" size="icon" onClick={onDuplicate} title="Duplicate proposal" data-testid={`duplicate-proposal-${p.proposal_id}`}>
            <Copy className="h-4 w-4 text-cyan-400" />
          </Button>
          <Button variant="ghost" size="icon" className="text-destructive" onClick={onDelete} data-testid={`delete-proposal-${p.proposal_id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

// ===========================================================================
// EDITOR MODAL
// ===========================================================================
function ProposalEditor({ proposal, token, onClose, onSaved }) {
  const [p, setP] = useState(proposal);
  const [saving, setSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [offlineSignOpen, setOfflineSignOpen] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => { setP(proposal); }, [proposal]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/venue-proposals/${p.proposal_id}`, patch, auth);
      setP(res.data);
      onSaved(res.data);
      toast.success('Saved');
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const copyPublicUrl = async () => {
    const url = `${window.location.origin}/venue-proposal/${p.token}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied');
      } else {
        const ta = document.createElement('textarea');
        ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        try { document.execCommand('copy'); toast.success('Link copied'); }
        finally { document.body.removeChild(ta); }
      }
    } catch (e) {
      toast.info(`Copy this link: ${url}`);
    }
  };

  const pdfUrl = `${API}/venue-proposals/${p.proposal_id}/pdf`;
  const pdfDownload = async () => {
    try {
      const res = await axios.get(pdfUrl, { ...auth, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl; link.download = `proposal_${p.proposal_id}.pdf`;
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error('PDF download failed'); }
  };

  const addToProforma = async (on) => {
    try {
      await axios.post(`${API}/venue-proposals/${p.proposal_id}/add-to-proforma`, { on }, auth);
      setP({ ...p, added_to_proforma: on });
      toast.success(on ? 'Mirrored to Production Manager budget' : 'Removed from budget');
    } catch (e) { toast.error('Toggle failed'); }
  };

  const reply = async (message) => {
    try {
      const res = await axios.post(`${API}/venue-proposals/${p.proposal_id}/comment`, { body: message }, auth);
      setP({ ...p, comments: [...(p.comments || []), res.data] });
    } catch (e) { toast.error('Reply failed'); }
  };

  const splitPreview = () => {
    const snap = p.proforma_snapshot || {};
    return {
      venue_take: snap.venue_take_cents || 0,
      eventism_take: snap.eventism_take_cents || 0,
      cac_cents: snap.cac_cents || 0,
      cac_ratio_pct: snap.cac_ratio_pct || 0,
      risk_level: snap.risk_level || 'unknown',
      risk_label: snap.risk_label || '—',
      risk_note: snap.risk_note || '',
    };
  };
  const split = splitPreview();

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-card max-w-[1800px] w-[98vw] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-8">
            <span className="truncate">{p.title}</span>
            <div className="flex items-center gap-2">
              <Badge className={STATUS_META[p.status]?.color}>{STATUS_META[p.status]?.label || p.status}</Badge>
              {p.signature?.signed && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30"><Check className="h-3 w-3 mr-0.5" />Signed</Badge>}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-3 gap-5">
          <div className="md:col-span-2 space-y-5">
            {/* Basics */}
            <Card><CardContent className="p-4 space-y-3">
              <h3 className="text-sm font-semibold">Recipient & Venue</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Venue Name"><Input value={p.venue_name || ''} onChange={(e) => setP({ ...p, venue_name: e.target.value })} onBlur={() => save({ venue_name: p.venue_name })} data-testid="editor-venue-name" /></Field>
                <Field label="Venue Address"><Input value={p.venue_address || ''} onChange={(e) => setP({ ...p, venue_address: e.target.value })} onBlur={() => save({ venue_address: p.venue_address })} placeholder="1234 Blake St, Denver, CO" data-testid="editor-venue-address" /></Field>
                <Field label="Contact Name"><Input value={p.venue_contact_name || ''} onChange={(e) => setP({ ...p, venue_contact_name: e.target.value })} onBlur={() => save({ venue_contact_name: p.venue_contact_name })} /></Field>
                <Field label="Venue Email"><Input value={p.venue_email || ''} onChange={(e) => setP({ ...p, venue_email: e.target.value })} onBlur={() => save({ venue_email: p.venue_email })} /></Field>
                <Field label="Contact Phone"><Input value={p.venue_contact_phone || ''} onChange={(e) => setP({ ...p, venue_contact_phone: e.target.value })} onBlur={() => save({ venue_contact_phone: p.venue_contact_phone })} placeholder="720-555-0100" data-testid="editor-venue-phone" /></Field>
                <Field label="Venue Logo URL"><Input value={p.venue_logo_url || ''} onChange={(e) => setP({ ...p, venue_logo_url: e.target.value })} onBlur={() => save({ venue_logo_url: p.venue_logo_url })} placeholder="https://venue.com/logo.png" data-testid="editor-venue-logo" /></Field>
                <Field label="Proposal Type">
                  <Select value={p.proposal_type} onValueChange={(v) => save({ proposal_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="partnership">Partnership</SelectItem>
                      <SelectItem value="venue_rental">Venue Rental</SelectItem>
                      <SelectItem value="rfp">RFP</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Title (shown on landing + PDF)"><Input value={p.title || ''} onChange={(e) => setP({ ...p, title: e.target.value })} onBlur={() => save({ title: p.title })} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Hero image URL"><Input value={p.hero_image_url || ''} onChange={(e) => setP({ ...p, hero_image_url: e.target.value })} onBlur={() => save({ hero_image_url: p.hero_image_url })} placeholder="https://…" /></Field>
                <Field label="Target Attendance">
                  <Input value={p.target_attendance || ''} onChange={(e) => setP({ ...p, target_attendance: e.target.value })} onBlur={() => save({ target_attendance: p.target_attendance })} placeholder="e.g. 500 guests · upscale crowd" data-testid="editor-target-attendance" />
                </Field>
              </div>
            </CardContent></Card>

            {/* Flyer / additional images */}
            <FlyerImagesEditor
              urls={p.flyer_images || []}
              onSave={(arr) => save({ flyer_images: arr })}
            />

            {/* Proposed Dates — editable list */}
            <ProposedDatesEditor
              dates={p.proposed_dates || []}
              onSave={(arr) => save({ proposed_dates: arr })}
            />

            {/* Content */}
            <Card><CardContent className="p-4 space-y-3">
              <h3 className="text-sm font-semibold">Content</h3>
              <Field label="Executive Summary">
                <RichTextEditor
                  value={p.executive_summary || ''}
                  onChange={(html) => setP({ ...p, executive_summary: html })}
                  minHeight={160}
                />
                <div className="flex justify-end mt-1">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => save({ executive_summary: p.executive_summary })} data-testid="save-exec-summary">Save</Button>
                </div>
              </Field>
              <Field label="Event Overview">
                <RichTextEditor
                  value={p.event_overview || ''}
                  onChange={(html) => setP({ ...p, event_overview: html })}
                  minHeight={200}
                />
                <div className="flex justify-end mt-1">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => save({ event_overview: p.event_overview })} data-testid="save-event-overview">Save</Button>
                </div>
              </Field>
              <Field label="About Eventism">
                <RichTextEditor
                  value={p.about_eventism || ''}
                  onChange={(html) => setP({ ...p, about_eventism: html })}
                  minHeight={160}
                />
                <div className="flex justify-end mt-1">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => save({ about_eventism: p.about_eventism })} data-testid="save-about-eventism">Save</Button>
                </div>
              </Field>
              <Field label="About Other Organizers (optional)">
                <RichTextEditor
                  value={p.about_organizers || ''}
                  onChange={(html) => setP({ ...p, about_organizers: html })}
                  minHeight={120}
                />
                <div className="flex justify-end mt-1">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => save({ about_organizers: p.about_organizers })} data-testid="save-about-organizers">Save</Button>
                </div>
              </Field>
            </CardContent></Card>

            <BulletListEditor
              title="Eventism Responsibilities"
              items={p.eventism_responsibilities || []}
              onSave={(arr) => save({ eventism_responsibilities: arr })}
              testid="eventism-resp"
            />
            <BulletListEditor
              title="Venue Responsibilities"
              items={p.venue_responsibilities || []}
              onSave={(arr) => save({ venue_responsibilities: arr })}
              testid="venue-resp"
            />

            {/* Accommodations — especially for RFP; editable for all types */}
            <BulletListEditor
              title={p.proposal_type === 'rfp' ? 'Required Venue Accommodations (RFP)' : 'Accommodations & House Requirements'}
              items={p.accommodations || []}
              onSave={(arr) => save({ accommodations: arr })}
              testid="accommodations"
              icon={Building2}
            />

            <CommentsThread comments={p.comments || []} onReply={reply} />
          </div>

          {/* Right sidebar */}
          <div className="space-y-4">
            <Card><CardContent className="p-4 space-y-3">
              <h3 className="text-sm font-semibold">Pro Forma</h3>
              <div className="flex items-center gap-3">
                <Switch checked={!!p.include_proforma} onCheckedChange={(v) => save({ include_proforma: v })} data-testid="toggle-include-proforma" />
                <span className="text-xs">Include in proposal</span>
              </div>
              {p.include_proforma && (
                <Field label="Mode">
                  <Select value={p.proforma_mode} onValueChange={(v) => save({ proforma_mode: v })}>
                    <SelectTrigger data-testid="select-proforma-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_budget">Full Budget (income · expense · net)</SelectItem>
                      <SelectItem value="partnership">Partnership (ticket rev + venue share)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              )}
              {p.proposal_type === 'partnership' && (
                <Field label="Venue revenue share %">
                  <Input type="number" min="0" max="100" step="0.5" value={p.venue_revenue_share_pct ?? 0}
                    onChange={(e) => setP({ ...p, venue_revenue_share_pct: parseFloat(e.target.value || 0) })}
                    onBlur={() => save({ venue_revenue_share_pct: p.venue_revenue_share_pct })} />
                </Field>
              )}
              {p.proposal_type === 'venue_rental' && (
                <Field label="Rental fee ($)">
                  <Input type="number" min="0" step="1"
                    value={(p.venue_rental_fee_cents || 0) / 100}
                    onChange={(e) => setP({ ...p, venue_rental_fee_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                    onBlur={() => save({ venue_rental_fee_cents: p.venue_rental_fee_cents })} />
                </Field>
              )}
              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Switch checked={!!p.added_to_proforma} onCheckedChange={addToProforma} data-testid="toggle-add-to-proforma" />
                <span className="text-xs">Mirror venue fee into Production Manager budget</span>
              </div>
            </CardContent></Card>

            {/* CAC & Risk preview (replaces revenue-share framing) */}
            {p.include_proforma && p.proforma_snapshot && (
              <Card><CardContent className="p-4 space-y-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  {split.risk_level === 'low' && <ShieldCheck className="w-4 h-4 text-emerald-400" />}
                  {split.risk_level === 'moderate' && <Shield className="w-4 h-4 text-amber-400" />}
                  {split.risk_level === 'elevated' && <Shield className="w-4 h-4 text-orange-400" />}
                  {split.risk_level === 'high' && <ShieldAlert className="w-4 h-4 text-rose-400" />}
                  {split.risk_level === 'unknown' && <Shield className="w-4 h-4 text-muted-foreground" />}
                  CAC & Risk Preview
                </h3>
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-muted-foreground">CAC · Cost to Acquire</span>
                  <span className="text-xl font-bold tabular-nums" data-testid="editor-cac-value">
                    {split.cac_cents > 0 ? `$${(split.cac_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-muted-foreground">Ratio of avg. ticket</span>
                  <span className="text-sm tabular-nums">{split.cac_ratio_pct > 0 ? `${split.cac_ratio_pct}%` : '—'}</span>
                </div>
                <div className={`p-2.5 rounded-md text-xs mt-2 ${
                  split.risk_level === 'low' ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30' :
                  split.risk_level === 'moderate' ? 'bg-amber-500/10 text-amber-300 border border-amber-500/30' :
                  split.risk_level === 'elevated' ? 'bg-orange-500/10 text-orange-300 border border-orange-500/30' :
                  split.risk_level === 'high' ? 'bg-rose-500/10 text-rose-300 border border-rose-500/30' :
                  'bg-muted/40 text-muted-foreground'
                }`} data-testid="editor-risk-card">
                  <div className="font-semibold">Risk tolerance: {split.risk_label}</div>
                  {split.risk_note && <div className="mt-0.5">{split.risk_note}</div>}
                </div>
                <p className="text-[10px] text-muted-foreground pt-1 border-t border-border">
                  Based on tagged marketing lines + forecast attendance. Updates on save.
                </p>
              </CardContent></Card>
            )}

            <Card><CardContent className="p-4 space-y-3">
              <h3 className="text-sm font-semibold">Share</h3>
              <div className="flex gap-2">
                <Button onClick={() => setEmailOpen(true)} disabled={saving} className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="open-send-email">
                  <Mail className="h-4 w-4 mr-1" /> {p.status === 'draft' ? 'Send via Email' : 'Resend Email'}
                </Button>
                <Button variant="outline" onClick={copyPublicUrl} data-testid="copy-public-url" title="Copy public link">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <Button variant="outline" className="w-full" onClick={pdfDownload} data-testid="download-pdf">
                <Download className="h-4 w-4 mr-1" /> Download PDF
              </Button>
              <a href={`/venue-proposal/${p.token}`} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-400 hover:underline flex items-center gap-1">
                <ExternalLink className="h-3 w-3" /> Open public landing
              </a>
              {p.signature?.signed && (
                <div className="text-xs p-2 rounded bg-emerald-500/10 border border-emerald-500/30 space-y-0.5">
                  <div className="flex items-center gap-1 text-emerald-300 font-semibold">
                    <FileCheck2 className="h-3 w-3" />
                    Signed{p.signature.signed_via === 'offline' ? ' (offline)' : ''}
                  </div>
                  <div>{p.signature.signer_name} ({p.signature.signer_title || '—'})</div>
                  <div className="text-muted-foreground">{p.signature.signed_at ? new Date(p.signature.signed_at).toLocaleString() : ''}{p.signature.ip ? ` · IP ${p.signature.ip}` : ''}</div>
                  {p.signature.signed_pdf_path && (
                    <a href={`${API}/files/${p.signature.signed_pdf_path}`} target="_blank" rel="noopener noreferrer" className="text-emerald-300 underline flex items-center gap-1 mt-1" data-testid="signed-pdf-link">
                      <Download className="h-3 w-3" /> Signed PDF
                    </a>
                  )}
                  {p.signature.notes && <div className="text-muted-foreground italic mt-1">"{p.signature.notes}"</div>}
                </div>
              )}
              {!p.signature?.signed && (
                <>
                  <Button
                    variant="outline"
                    className="w-full border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                    onClick={() => setOfflineSignOpen(true)}
                    data-testid="open-mark-signed-offline"
                  >
                    <FileCheck2 className="h-4 w-4 mr-1" /> Mark as Signed Offline
                  </Button>
                  {p.status === 'viewed' && (
                    <div className="text-xs p-2 rounded bg-amber-500/10 border border-amber-500/30 flex items-center gap-1 text-amber-300">
                      <FileClock className="h-3 w-3" /> Viewed · awaiting signature
                    </div>
                  )}
                </>
              )}
              {(p.email_sent_log || []).length > 0 && (
                <div className="text-[11px] text-muted-foreground pt-2 border-t border-border">
                  <div className="font-semibold mb-1">Email log</div>
                  {p.email_sent_log.slice(-3).reverse().map((e, i) => (
                    <div key={i} className="truncate">
                      → {e.to_email} · {new Date(e.sent_at).toLocaleDateString()} {e.delivered ? '✓' : '⚠'}
                    </div>
                  ))}
                </div>
              )}
            </CardContent></Card>

            {/* Analytics */}
            <AnalyticsCard p={p} />
          </div>
        </div>
      </DialogContent>
      {emailOpen && (
        <SendEmailDialog
          p={p}
          token={token}
          onClose={(updated) => {
            setEmailOpen(false);
            if (updated) { setP(updated); onSaved(updated); }
          }}
        />
      )}
      {offlineSignOpen && (
        <MarkSignedOfflineDialog
          p={p}
          token={token}
          onClose={(updated) => {
            setOfflineSignOpen(false);
            if (updated) { setP(updated); onSaved(updated); }
          }}
        />
      )}
    </Dialog>
  );
}

function SplitRow({ label, cents, color }) {
  return (
    <div className="flex justify-between text-sm py-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-semibold tabular-nums ${color}`}>${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
    </div>
  );
}

function AnalyticsCard({ p }) {
  const fmtDT = (iso) => iso ? new Date(iso).toLocaleString() : '—';
  const since = (iso) => {
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const h = Math.floor(mins / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };
  const viewCount = p.view_count || 0;
  const commentsCount = (p.comments || []).length;
  const emailsSent = (p.email_sent_log || []).length;
  return (
    <Card data-testid="proposal-analytics">
      <CardContent className="p-4 space-y-3">
        <h3 className="text-sm font-semibold flex items-center gap-1">
          <BarChart3 className="h-4 w-4 text-indigo-400" /> Analytics
        </h3>
        <div className="grid grid-cols-3 gap-2 text-center">
          <MiniStat label="Views" value={viewCount} />
          <MiniStat label="Comments" value={commentsCount} />
          <MiniStat label="Emails" value={emailsSent} />
        </div>
        <div className="space-y-1.5 text-[11px] pt-2 border-t border-border">
          <Row2 k="Sent at" v={fmtDT(p.sent_at)} hint={since(p.sent_at)} />
          <Row2 k="First viewed" v={fmtDT(p.viewed_at)} hint={since(p.viewed_at)} accent={p.viewed_at ? 'text-amber-300' : ''} />
          <Row2 k="Last viewed" v={fmtDT(p.last_viewed_at)} hint={since(p.last_viewed_at)} />
          {p.approved_at && <Row2 k="Approved at" v={fmtDT(p.approved_at)} hint={since(p.approved_at)} accent="text-emerald-300" />}
          {p.declined_at && <Row2 k="Declined at" v={fmtDT(p.declined_at)} hint={since(p.declined_at)} accent="text-rose-300" />}
        </div>
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="p-2 rounded bg-muted/40 border border-border">
      <div className="text-lg font-bold tabular-nums" data-testid={`analytics-${label.toLowerCase()}`}>{value}</div>
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
    </div>
  );
}

function Row2({ k, v, hint, accent }) {
  if (v === '—') return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-muted-foreground">—</span>
    </div>
  );
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground flex-shrink-0">{k}</span>
      <span className={`text-right truncate ${accent || ''}`} title={v}>
        {hint ? <><span className="font-medium">{hint}</span> <span className="text-muted-foreground">({v})</span></> : v}
      </span>
    </div>
  );
}

function FlyerImagesEditor({ urls, onSave }) {
  const [local, setLocal] = useState(urls);
  const [draft, setDraft] = useState('');
  useEffect(() => setLocal(urls), [urls]);
  const add = () => {
    const v = (draft || '').trim();
    if (!v) return;
    const next = [...local, v];
    setLocal(next); onSave(next); setDraft('');
  };
  const remove = (idx) => {
    const next = local.filter((_, i) => i !== idx);
    setLocal(next); onSave(next);
  };
  return (
    <Card><CardContent className="p-4 space-y-3" data-testid="flyer-images-editor">
      <h3 className="text-sm font-semibold">Event Flyer & Additional Images</h3>
      <div className="grid grid-cols-4 gap-2">
        {local.map((u, i) => (
          <div key={i} className="relative group aspect-square rounded-md overflow-hidden bg-muted">
            <img src={u} alt="" className="w-full h-full object-cover" />
            <button onClick={() => remove(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity">×</button>
          </div>
        ))}
        {local.length === 0 && (
          <div className="col-span-4 text-xs text-muted-foreground py-3 text-center border border-dashed border-border rounded-md">
            Add a flyer image URL or additional event photos below.
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Input placeholder="https://image-url.com/flyer.png" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} data-testid="flyer-image-input" />
        <Button onClick={add} size="sm" data-testid="flyer-image-add"><Plus className="h-4 w-4" /></Button>
      </div>
    </CardContent></Card>
  );
}

function SendEmailDialog({ p, token, onClose }) {
  const [form, setForm] = useState({
    to_name: p.venue_contact_name || '',
    to_email: p.venue_email || '',
    subject: `${p.title} — from Eventism`,
    body: `We'd love to partner with ${p.venue_name || 'your venue'} on ${p.event_title}. This proposal covers proposed dates, responsibilities, media kit and a full pro-forma plan. Click below to review, post questions, and sign — no login required.`,
  });
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const submit = async () => {
    if (!form.to_email) { toast.error('Recipient email required'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/venue-proposals/${p.proposal_id}/send-email`, {
        to_email: form.to_email,
        to_name: form.to_name,
        subject: form.subject,
        body: form.body,
      }, auth);
      toast.success(res.data.delivered ? 'Email sent!' : 'Email queued (Resend not configured on this env)');
      // Reload the proposal to pull updated email_sent_log + status
      const fresh = await axios.get(`${API}/venue-proposals/${p.proposal_id}`, auth);
      onClose(fresh.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={() => onClose(null)}>
      <DialogContent className="bg-card max-w-xl">
        <DialogHeader><DialogTitle>Send proposal by email</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="To Name"><Input value={form.to_name} onChange={(e) => setForm({ ...form, to_name: e.target.value })} placeholder="Alex Lee" data-testid="email-to-name" /></Field>
            <Field label="To Email *"><Input type="email" value={form.to_email} onChange={(e) => setForm({ ...form, to_email: e.target.value })} placeholder="events@venue.com" data-testid="email-to" /></Field>
          </div>
          <Field label="Subject"><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="email-subject" /></Field>
          <Field label="Message"><Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="email-body" /></Field>
          <p className="text-[11px] text-muted-foreground">A branded "Review & Sign Proposal" button linking to the magic URL is auto-added below your message.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(null)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="email-send">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkSignedOfflineDialog({ p, token, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    signer_name: p.venue_contact_name || '',
    signer_title: '',
    signed_date: today,
    notes: '',
    signed_pdf_path: '',
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pdfFilename, setPdfFilename] = useState('');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, {
        ...auth,
        headers: { ...auth.headers, 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, signed_pdf_path: res.data.path }));
      setPdfFilename(file.name);
      toast.success('Signed PDF uploaded');
    } catch (e) {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.signer_name.trim()) { toast.error('Signer name is required'); return; }
    if (!form.signed_date) { toast.error('Date signed is required'); return; }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/venue-proposals/${p.proposal_id}/mark-signed-offline`,
        form,
        auth,
      );
      toast.success(`🎉 ${p.venue_name || 'Venue'} marked as signed! CRM and vendor records updated.`);
      onClose(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark signed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose(null)}>
      <DialogContent className="bg-card max-w-xl" data-testid="mark-signed-offline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck2 className="h-5 w-5 text-emerald-400" />
            Mark as Signed Offline
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-200">
            Use this when the venue downloaded the PDF, signed it offline, and returned it.
            We'll mark the proposal approved, promote the CRM lead to <strong>won</strong>,
            and auto-create a vendor record on the linked event.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Signer name *">
              <Input
                value={form.signer_name}
                onChange={(e) => setForm({ ...form, signer_name: e.target.value })}
                placeholder="Susan Blanchard"
                data-testid="offline-signer-name"
              />
            </Field>
            <Field label="Signer title">
              <Input
                value={form.signer_title}
                onChange={(e) => setForm({ ...form, signer_title: e.target.value })}
                placeholder="COO"
                data-testid="offline-signer-title"
              />
            </Field>
          </div>
          <Field label="Date signed *">
            <Input
              type="date"
              value={form.signed_date}
              onChange={(e) => setForm({ ...form, signed_date: e.target.value })}
              data-testid="offline-signed-date"
            />
          </Field>
          <Field label="Signed PDF (countersigned copy)">
            <div className="flex items-center gap-2">
              <input
                id="offline-pdf-upload"
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => uploadPdf(e.target.files?.[0])}
                className="hidden"
              />
              <label
                htmlFor="offline-pdf-upload"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-background/50 hover:bg-muted cursor-pointer text-sm"
                data-testid="offline-pdf-button"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 rotate-180" />}
                {uploading ? 'Uploading…' : (form.signed_pdf_path ? 'Replace file' : 'Upload signed PDF')}
              </label>
              {form.signed_pdf_path && (
                <span className="text-xs text-emerald-300 truncate flex-1" data-testid="offline-pdf-name">
                  ✓ {pdfFilename || 'Uploaded'}
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">PDF or image. Stored encrypted in object storage.</p>
          </Field>
          <Field label="Notes (optional)">
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Signed at 3/4 dinner meeting. Rep negotiated $0 rental for first season."
              data-testid="offline-notes"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(null)} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || uploading}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="offline-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileCheck2 className="h-4 w-4 mr-1" />}
            Mark as Signed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulletListEditor({ title, items, onSave, testid, icon: Icon }) {
  const [local, setLocal] = useState(items);
  useEffect(() => setLocal(items), [items]);
  const update = (idx, v) => setLocal(local.map((x, i) => (i === idx ? v : x)));
  const add = () => setLocal([...local, '']);
  const remove = (idx) => {
    const next = local.filter((_, i) => i !== idx);
    setLocal(next); onSave(next);
  };
  const commit = () => onSave(local.filter((x) => (x || '').trim()));
  return (
    <Card><CardContent className="p-4 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          {Icon && <Icon className="h-4 w-4 text-fuchsia-500" />}
          {title}
        </h3>
        <Button size="sm" variant="ghost" onClick={add}><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="space-y-2">
        {local.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <Input value={item} onChange={(e) => update(idx, e.target.value)} onBlur={commit} placeholder="Bullet item…" />
            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(idx)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function ProposedDatesEditor({ dates, onSave }) {
  const [local, setLocal] = useState(dates || []);
  useEffect(() => setLocal(dates || []), [dates]);

  const update = (idx, field, val) => {
    const next = local.map((d, i) => (i === idx ? { ...d, [field]: val } : d));
    setLocal(next);
  };
  const commit = (next) => {
    const cleaned = (next || local).filter((d) => d.date);
    onSave(cleaned);
  };
  const add = () => {
    const next = [...local, { date: '', start_time: '', end_time: '', label: '', description: '' }];
    setLocal(next);
  };
  const remove = (idx) => {
    const next = local.filter((_, i) => i !== idx);
    setLocal(next); commit(next);
  };

  return (
    <Card><CardContent className="p-4 space-y-3" data-testid="proposed-dates-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <CalendarIcon className="h-4 w-4 text-indigo-500" />
          Proposed Dates &amp; Times
        </h3>
        <Button size="sm" variant="ghost" onClick={add} data-testid="add-date-btn"><Plus className="h-4 w-4 mr-1" /> Add date</Button>
      </div>
      {local.length === 0 && (
        <p className="text-xs text-muted-foreground">No dates yet. Add at least one so the venue knows when you want the event.</p>
      )}
      <div className="space-y-2">
        {local.map((d, idx) => (
          <div key={idx} className="p-3 rounded-lg border border-border bg-muted/30 space-y-2" data-testid={`date-row-${idx}`}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <div className="text-[10px] uppercase text-muted-foreground mb-1">Date</div>
                <Input type="date" value={d.date || ''} onChange={(e) => update(idx, 'date', e.target.value)} onBlur={() => commit()} data-testid={`date-input-${idx}`} />
              </div>
              <div>
                <div className="text-[10px] uppercase text-muted-foreground mb-1">Start time</div>
                <Input type="time" value={d.start_time || ''} onChange={(e) => update(idx, 'start_time', e.target.value)} onBlur={() => commit()} />
              </div>
              <div>
                <div className="text-[10px] uppercase text-muted-foreground mb-1">End time</div>
                <Input type="time" value={d.end_time || ''} onChange={(e) => update(idx, 'end_time', e.target.value)} onBlur={() => commit()} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="sm:col-span-1">
                <div className="text-[10px] uppercase text-muted-foreground mb-1">Label</div>
                <Input value={d.label || ''} onChange={(e) => update(idx, 'label', e.target.value)} onBlur={() => commit()} placeholder="First choice" />
              </div>
              <div className="sm:col-span-2">
                <div className="text-[10px] uppercase text-muted-foreground mb-1">Notes</div>
                <Input value={d.description || ''} onChange={(e) => update(idx, 'description', e.target.value)} onBlur={() => commit()} placeholder="Opens 7pm · doors at 6:30" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button size="sm" variant="ghost" className="text-destructive h-7 text-xs" onClick={() => remove(idx)} data-testid={`remove-date-${idx}`}>
                <XIcon className="h-3.5 w-3.5 mr-1" /> Remove
              </Button>
            </div>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function CommentsThread({ comments, onReply }) {
  const [draft, setDraft] = useState('');
  const submit = async () => {
    if (!draft.trim()) return;
    await onReply(draft.trim());
    setDraft('');
  };
  return (
    <Card><CardContent className="p-4 space-y-3">
      <h3 className="text-sm font-semibold flex items-center gap-1">
        <MessageSquare className="h-4 w-4" /> Comments & Questions ({comments.length})
      </h3>
      {comments.length === 0 ? (
        <p className="text-xs text-muted-foreground">No comments yet. The venue can post notes from the public landing page.</p>
      ) : (
        <div className="space-y-2">
          {comments.map((c) => (
            <div key={c.id} className={`p-2.5 rounded-md text-xs ${c.author_type === 'admin' ? 'bg-indigo-500/10 border border-indigo-500/20' : 'bg-muted border border-border'}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold">{c.author_name} <span className="text-[10px] text-muted-foreground">({c.author_type})</span></span>
                <span className="text-[10px] text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap">{c.body}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 pt-2 border-t border-border">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Reply as Eventism…" onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} data-testid="admin-reply-input" />
        <Button onClick={submit} size="sm" data-testid="admin-reply-send">Reply</Button>
      </div>
    </CardContent></Card>
  );
}
